import React, { useState, useEffect } from 'react';
import { Settings, Plus, Edit3, Trash2, Search, FolderTree, AlertCircle, CheckCircle, Info, Package, Edit2, Beaker, Tags } from 'lucide-react';
import CategoryForm from '../CategoryForm';
import ProductServiceForm from '../ProductServiceForm';
import { crudOperations } from '../../../../services/api';
import ConfirmDialog from '../../../ConfirmDialog';
import useConfirm from '../../../../hooks/useConfirm';

/**
 * AdminTab component - Admin-only settings and management
 * Provides interface for managing job categories and other admin functions
 */
const AdminTab = ({ userProfile, showNotification }) => {
  const { confirmDialog, confirm } = useConfirm();
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('categories');

  // Job Categories state
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  // Products/Services state
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAddProductForm, setShowAddProductForm] = useState(false);
  const [showEditProductForm, setShowEditProductForm] = useState(false);

  // Treatment Product Categories state
  const [treatmentCategories, setTreatmentCategories] = useState([]);
  const [treatmentCategoriesLoading, setTreatmentCategoriesLoading] = useState(true);
  const [treatmentCategorySearch, setTreatmentCategorySearch] = useState('');
  const [editingTreatmentCategory, setEditingTreatmentCategory] = useState(null);
  const [showAddTreatmentCategory, setShowAddTreatmentCategory] = useState(false);
  const [treatmentCategoryForm, setTreatmentCategoryForm] = useState({ name: '', sort_order: 0 });

  // Treatment Products state
  const [treatmentProducts, setTreatmentProducts] = useState([]);
  const [treatmentProductsLoading, setTreatmentProductsLoading] = useState(true);
  const [treatmentProductSearch, setTreatmentProductSearch] = useState('');
  const [editingTreatmentProduct, setEditingTreatmentProduct] = useState(null);
  const [showAddTreatmentProduct, setShowAddTreatmentProduct] = useState(false);
  const [treatmentProductForm, setTreatmentProductForm] = useState({ name: '', category_id: '', active_ingredients: '' });

  useEffect(() => {
    if (activeSection === 'categories') {
      loadJobCategories();
    } else if (activeSection === 'services') {
      loadProductsServices();
    } else if (activeSection === 'treatmentCategories') {
      loadTreatmentCategories();
    } else if (activeSection === 'treatmentProducts') {
      loadTreatmentCategories(); // need categories for dropdown
      loadTreatmentProducts();
    }
  }, [activeSection]);

  const loadJobCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await crudOperations.get('admin/job-categories');
      
      if (response.data.success) {
        setCategories(response.data.data.categories || []);
      } else {
        showNotification('Failed to load job categories', 'error');
      }
    } catch (error) {
      console.error('Error loading job categories:', error);
      showNotification(error.message || 'Failed to load job categories', 'error');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const loadProductsServices = async () => {
    try {
      setProductsLoading(true);
      const response = await crudOperations.get('admin/products-services');
      
      if (response.data.success && response.data.data.products) {
        setProducts(response.data.data.products);
      } else {
        showNotification('Failed to load products/services', 'error');
      }
    } catch (error) {
      console.error('Error loading products/services:', error);
      showNotification(error.message || 'Failed to load products/services', 'error');
    } finally {
      setProductsLoading(false);
    }
  };

  const loadTreatmentCategories = async () => {
    try {
      setTreatmentCategoriesLoading(true);
      const response = await crudOperations.get('admin/treatment-product-categories');
      if (response.data.success) {
        setTreatmentCategories(response.data.data.categories || []);
      }
    } catch (error) {
      console.error('Error loading treatment categories:', error);
      showNotification('Failed to load treatment categories', 'error');
    } finally {
      setTreatmentCategoriesLoading(false);
    }
  };

  const loadTreatmentProducts = async () => {
    try {
      setTreatmentProductsLoading(true);
      const response = await crudOperations.get('admin/treatment-products');
      if (response.data.success) {
        setTreatmentProducts(response.data.data.products || []);
      }
    } catch (error) {
      console.error('Error loading treatment products:', error);
      showNotification('Failed to load treatment products', 'error');
    } finally {
      setTreatmentProductsLoading(false);
    }
  };

  const filteredCategories = categories.filter(category =>
    category.category_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.full_path.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => {
    if (!searchTerm) return 0;
    const term = searchTerm.toLowerCase();
    const aStarts = a.category_name.toLowerCase().startsWith(term);
    const bStarts = b.category_name.toLowerCase().startsWith(term);
    if (aStarts && !bStarts) return -1;
    if (!aStarts && bStarts) return 1;
    return a.category_name.localeCompare(b.category_name);
  });

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    product.item_code?.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    product.sales_description?.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    product.category_level1?.toLowerCase().includes(productSearchTerm.toLowerCase())
  ).sort((a, b) => {
    if (!productSearchTerm) return 0;
    const term = productSearchTerm.toLowerCase();
    const aStartsWithName = a.name.toLowerCase().startsWith(term);
    const bStartsWithName = b.name.toLowerCase().startsWith(term);
    if (aStartsWithName && !bStartsWithName) return -1;
    if (!aStartsWithName && bStartsWithName) return 1;
    return a.name.localeCompare(b.name);
  });

  const filteredTreatmentCategories = treatmentCategories.filter(c =>
    c.name.toLowerCase().includes(treatmentCategorySearch.toLowerCase())
  );

  const filteredTreatmentProducts = treatmentProducts.filter(p =>
    p.name.toLowerCase().includes(treatmentProductSearch.toLowerCase()) ||
    p.active_ingredients?.toLowerCase().includes(treatmentProductSearch.toLowerCase()) ||
    p.category_name?.toLowerCase().includes(treatmentProductSearch.toLowerCase())
  );

  const renderCategoryManagement = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Job Categories Management</h3>
          <p className="text-gray-600 text-sm">
            Manage job categories that technicians can select when creating jobs.
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-pest-blue hover:bg-pest-blue-dark text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
        >
          <Plus size={18} />
          Add Category
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
          placeholder="Search categories by name, path, or description..."
        />
      </div>

      {/* Categories List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {categoriesLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pest-blue mx-auto mb-4"></div>
            <p className="text-gray-500">Loading job categories...</p>
          </div>
        ) : filteredCategories.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredCategories.map((category) => (
              <div
                key={category.id}
                className="p-4 hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <FolderTree className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900">
                        {category.category_name}
                      </span>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        Level {category.category_level}
                      </span>
                      {!category.is_active && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Path:</strong> {category.full_path}
                    </p>

                    {category.qb_class && (
                      <p className="text-xs text-gray-500 mb-1">
                        QB Class: <span className="text-gray-700">{category.qb_class}</span>
                      </p>
                    )}

                    {category.description && (
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Description:</strong> {category.description}
                      </p>
                    )}

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>Code: {category.category_code}</span>
                      <span>Sort: {category.sort_order}</span>
                      <span>Created: {new Date(category.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedCategory(category);
                        setShowEditForm(true);
                      }}
                      className="text-gray-400 hover:text-pest-blue transition-colors duration-200"
                      title="Edit category"
                    >
                      <Edit3 size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category)}
                      className="text-gray-400 hover:text-red-600 transition-colors duration-200"
                      title="Delete category"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No categories found</p>
            <p className="text-gray-400 text-sm">
              {searchTerm 
                ? 'Try adjusting your search terms'
                : 'Click "Add Category" to create your first category'
              }
            </p>
          </div>
        )}
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-800">Total Categories</p>
              <p className="text-2xl font-bold text-blue-900">{categories.length}</p>
            </div>
            <FolderTree className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-800">Active</p>
              <p className="text-2xl font-bold text-green-900">
                {categories.filter(c => c.is_active).length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-800">Level 1</p>
              <p className="text-2xl font-bold text-yellow-900">
                {categories.filter(c => c.category_level === 1).length}
              </p>
            </div>
            <Info className="w-8 h-8 text-yellow-600" />
          </div>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-800">Level 3</p>
              <p className="text-2xl font-bold text-purple-900">
                {categories.filter(c => c.category_level === 3).length}
              </p>
            </div>
            <Settings className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderProductsServicesManagement = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Invoice Products & Services</h3>
          <p className="text-gray-600 text-sm">
            Manage products and services used in invoices and job creation.
          </p>
        </div>
        <button
          onClick={() => setShowAddProductForm(true)}
          className="bg-pest-blue hover:bg-pest-blue-dark text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
        >
          <Plus size={18} />
          Add Product/Service
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={productSearchTerm}
          onChange={(e) => setProductSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
          placeholder="Search by name, code, description, or category..."
        />
      </div>

      {/* Products/Services List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {productsLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pest-blue mx-auto mb-4"></div>
            <p className="text-gray-500">Loading products/services...</p>
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="p-4 hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900">
                        {product.name}
                      </span>
                      {product.item_code && (
                        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
                          {product.item_code}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-1 rounded ${
                        product.item_type === 'Product' 
                          ? 'bg-green-100 text-green-600' 
                          : 'bg-purple-100 text-purple-600'
                      }`}>
                        {product.item_type}
                      </span>
                      {!product.is_active && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-600 mb-2">
                      {product.category_level1 && (
                        <div>
                          <strong>Category:</strong> {product.category_level1}
                        </div>
                      )}
                      {product.item_type === 'Product' && (
                        <div>
                          <strong>Qty on Hand:</strong> {product.quantity_on_hand || 0}
                        </div>
                      )}
                      <div>
                        <strong>Type:</strong> {product.item_type}
                      </div>
                    </div>
                    
                    {product.sales_description && (
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Description:</strong> {product.sales_description}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>Created: {new Date(product.created_at).toLocaleDateString()}</span>
                      {product.created_by_name && (
                        <span>By: {product.created_by_name}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end justify-between gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleToggleProductActive(product)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${product.is_active ? 'bg-green-600' : 'bg-gray-300'}`}
                      title={product.is_active ? 'Deactivate' : 'Activate'}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${product.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedProduct(product);
                          setShowEditProductForm(true);
                        }}
                        className="w-8 h-8 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors"
                        title="Edit product/service"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product)}
                        className="w-8 h-8 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors"
                        title="Delete product/service"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No products/services found</p>
            <p className="text-gray-400 text-sm">
              {productSearchTerm 
                ? 'Try adjusting your search terms'
                : 'Click "Add Product/Service" to create your first item'
              }
            </p>
          </div>
        )}
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-800">Total Items</p>
              <p className="text-2xl font-bold text-blue-900">{products.length}</p>
            </div>
            <Package className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-800">Active</p>
              <p className="text-2xl font-bold text-green-900">
                {products.filter(p => p.is_active).length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-800">Services</p>
              <p className="text-2xl font-bold text-purple-900">
                {products.filter(p => p.item_type === 'Service').length}
              </p>
            </div>
            <Settings className="w-8 h-8 text-purple-600" />
          </div>
        </div>
        
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-800">Products</p>
              <p className="text-2xl font-bold text-orange-900">
                {products.filter(p => p.item_type === 'Product').length}
              </p>
            </div>
            <Package className="w-8 h-8 text-orange-600" />
          </div>
        </div>
      </div>
    </div>
  );

  // ── Treatment Product Categories ──

  const renderTreatmentCategoryManagement = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Treatment Product Categories</h3>
          <p className="text-gray-600 text-sm">
            Manage categories for treatment products used in treatment reports.
          </p>
        </div>
        <button
          onClick={() => { setTreatmentCategoryForm({ name: '', sort_order: treatmentCategories.length }); setShowAddTreatmentCategory(true); setEditingTreatmentCategory(null); }}
          className="bg-pest-blue hover:bg-pest-blue-dark text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
        >
          <Plus size={18} />
          Add Category
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={treatmentCategorySearch}
          onChange={(e) => setTreatmentCategorySearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
          placeholder="Search categories..."
        />
      </div>

      {/* Inline Add/Edit Form */}
      {(showAddTreatmentCategory || editingTreatmentCategory) && (
        <div className="bg-white border-2 border-pest-blue rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">
            {editingTreatmentCategory ? 'Edit Category' : 'Add Category'}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={treatmentCategoryForm.name}
                onChange={(e) => setTreatmentCategoryForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue"
                placeholder="e.g. Rodenticides"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input
                type="number"
                value={treatmentCategoryForm.sort_order}
                onChange={(e) => setTreatmentCategoryForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => { setShowAddTreatmentCategory(false); setEditingTreatmentCategory(null); }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveTreatmentCategory}
              disabled={!treatmentCategoryForm.name.trim()}
              className="px-4 py-2 bg-pest-blue hover:bg-pest-blue-dark text-white rounded-lg font-medium disabled:opacity-50"
            >
              {editingTreatmentCategory ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {treatmentCategoriesLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pest-blue mx-auto mb-4"></div>
            <p className="text-gray-500">Loading categories...</p>
          </div>
        ) : filteredTreatmentCategories.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredTreatmentCategories.map((cat) => (
              <div key={cat.id} className="p-4 hover:bg-gray-50 transition-colors duration-200">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Tags className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-900">{cat.name}</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      {cat.product_count || 0} products
                    </span>
                    <span className="text-xs text-gray-400">Sort: {cat.sort_order}</span>
                    {!cat.is_active && (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">Inactive</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleTreatmentCategoryActive(cat)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${cat.is_active ? 'bg-green-600' : 'bg-gray-300'}`}
                      title={cat.is_active ? 'Deactivate' : 'Activate'}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${cat.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <button
                      onClick={() => { setEditingTreatmentCategory(cat); setTreatmentCategoryForm({ name: cat.name, sort_order: cat.sort_order }); setShowAddTreatmentCategory(false); }}
                      className="w-8 h-8 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteTreatmentCategory(cat)}
                      className="w-8 h-8 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <Tags className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No categories found</p>
            <p className="text-gray-400 text-sm">
              {treatmentCategorySearch ? 'Try adjusting your search' : 'Click "Add Category" to create one'}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // ── Treatment Products ──

  const renderTreatmentProductManagement = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Treatment Products</h3>
          <p className="text-gray-600 text-sm">
            Manage products used in treatment reports. Each product belongs to a category and can include active ingredients.
          </p>
        </div>
        <button
          onClick={() => { setTreatmentProductForm({ name: '', category_id: treatmentCategories[0]?.id || '', active_ingredients: '' }); setShowAddTreatmentProduct(true); setEditingTreatmentProduct(null); }}
          className="bg-pest-blue hover:bg-pest-blue-dark text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
        >
          <Plus size={18} />
          Add Product
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={treatmentProductSearch}
          onChange={(e) => setTreatmentProductSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
          placeholder="Search by name, ingredients, or category..."
        />
      </div>

      {/* Inline Add/Edit Form */}
      {(showAddTreatmentProduct || editingTreatmentProduct) && (
        <div className="bg-white border-2 border-pest-blue rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">
            {editingTreatmentProduct ? 'Edit Product' : 'Add Product'}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
              <input
                type="text"
                value={treatmentProductForm.name}
                onChange={(e) => setTreatmentProductForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue"
                placeholder="e.g. Rascal Wax Blocks"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={treatmentProductForm.category_id}
                onChange={(e) => setTreatmentProductForm(f => ({ ...f, category_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue"
              >
                <option value="">Select category...</option>
                {treatmentCategories.filter(c => c.is_active).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Active Ingredients</label>
              <input
                type="text"
                value={treatmentProductForm.active_ingredients}
                onChange={(e) => setTreatmentProductForm(f => ({ ...f, active_ingredients: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue"
                placeholder="e.g. difenacoum 0.005% + denatonium benzoate 0.001%"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => { setShowAddTreatmentProduct(false); setEditingTreatmentProduct(null); }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveTreatmentProduct}
              disabled={!treatmentProductForm.name.trim() || !treatmentProductForm.category_id}
              className="px-4 py-2 bg-pest-blue hover:bg-pest-blue-dark text-white rounded-lg font-medium disabled:opacity-50"
            >
              {editingTreatmentProduct ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {treatmentProductsLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pest-blue mx-auto mb-4"></div>
            <p className="text-gray-500">Loading products...</p>
          </div>
        ) : filteredTreatmentProducts.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredTreatmentProducts.map((product) => (
              <div key={product.id} className="p-4 hover:bg-gray-50 transition-colors duration-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Beaker className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900">{product.name}</span>
                      <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded">
                        {product.category_name}
                      </span>
                      {!product.is_active && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">Inactive</span>
                      )}
                    </div>
                    {product.active_ingredients && (
                      <p className="text-sm text-gray-500 ml-6">
                        {product.active_ingredients}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleTreatmentProductActive(product)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${product.is_active ? 'bg-green-600' : 'bg-gray-300'}`}
                      title={product.is_active ? 'Deactivate' : 'Activate'}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${product.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingTreatmentProduct(product);
                        setTreatmentProductForm({ name: product.name, category_id: product.category_id, active_ingredients: product.active_ingredients || '' });
                        setShowAddTreatmentProduct(false);
                      }}
                      className="w-8 h-8 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteTreatmentProduct(product)}
                      className="w-8 h-8 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <Beaker className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No products found</p>
            <p className="text-gray-400 text-sm">
              {treatmentProductSearch ? 'Try adjusting your search' : 'Click "Add Product" to create one'}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // ── Treatment Category CRUD handlers ──

  const handleSaveTreatmentCategory = async () => {
    try {
      setLoading(true);
      const data = { name: treatmentCategoryForm.name.trim(), sort_order: treatmentCategoryForm.sort_order };
      const response = editingTreatmentCategory
        ? await crudOperations.put(`admin/treatment-product-categories/${editingTreatmentCategory.id}`, { ...data, is_active: editingTreatmentCategory.is_active })
        : await crudOperations.post('admin/treatment-product-categories', data);

      if (response.data.success) {
        showNotification(editingTreatmentCategory ? 'Category updated' : 'Category created');
        loadTreatmentCategories();
        setShowAddTreatmentCategory(false);
        setEditingTreatmentCategory(null);
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to save category';
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTreatmentCategoryActive = async (cat) => {
    try {
      await crudOperations.put(`admin/treatment-product-categories/${cat.id}`, {
        name: cat.name,
        sort_order: cat.sort_order,
        is_active: !cat.is_active
      });
      showNotification(cat.is_active ? 'Category deactivated' : 'Category activated');
      loadTreatmentCategories();
    } catch (error) {
      showNotification('Failed to update category', 'error');
    }
  };

  const handleDeleteTreatmentCategory = async (cat) => {
    const confirmed = await confirm({
      title: 'Delete Treatment Category',
      message: `Delete "${cat.name}"? ${cat.product_count > 0 ? `This category has ${cat.product_count} product(s) — you may need to deactivate instead.` : ''}`,
      variant: 'danger',
      confirmLabel: 'Delete'
    });
    if (!confirmed) return;
    try {
      setLoading(true);
      const response = await crudOperations.delete(`admin/treatment-product-categories/${cat.id}`);
      if (response.data.success) {
        showNotification('Category deleted');
        loadTreatmentCategories();
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to delete category';
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Treatment Product CRUD handlers ──

  const handleSaveTreatmentProduct = async () => {
    try {
      setLoading(true);
      const data = {
        name: treatmentProductForm.name.trim(),
        category_id: treatmentProductForm.category_id,
        active_ingredients: treatmentProductForm.active_ingredients.trim() || null
      };
      const response = editingTreatmentProduct
        ? await crudOperations.put(`admin/treatment-products/${editingTreatmentProduct.id}`, { ...data, is_active: editingTreatmentProduct.is_active })
        : await crudOperations.post('admin/treatment-products', data);

      if (response.data.success) {
        showNotification(editingTreatmentProduct ? 'Product updated' : 'Product created');
        loadTreatmentProducts();
        setShowAddTreatmentProduct(false);
        setEditingTreatmentProduct(null);
      }
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to save product', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTreatmentProductActive = async (product) => {
    try {
      await crudOperations.put(`admin/treatment-products/${product.id}`, {
        name: product.name,
        category_id: product.category_id,
        active_ingredients: product.active_ingredients,
        is_active: !product.is_active
      });
      showNotification(product.is_active ? 'Product deactivated' : 'Product activated');
      loadTreatmentProducts();
    } catch (error) {
      showNotification('Failed to update product', 'error');
    }
  };

  const handleDeleteTreatmentProduct = async (product) => {
    const confirmed = await confirm({
      title: 'Delete Treatment Product',
      message: `Permanently delete "${product.name}"?`,
      variant: 'danger',
      confirmLabel: 'Delete'
    });
    if (!confirmed) return;
    try {
      setLoading(true);
      const response = await crudOperations.delete(`admin/treatment-products/${product.id}`);
      if (response.data.success) {
        showNotification('Product deleted');
        loadTreatmentProducts();
      }
    } catch (error) {
      showNotification('Failed to delete product', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    const confirmed = await confirm({
      title: 'Delete Category',
      message: `This will ${category.is_active ? 'deactivate' : 'permanently delete'} "${category.category_name}".`,
      variant: 'danger',
      confirmLabel: 'Delete'
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await crudOperations.delete(`admin/job-categories/${category.id}`);

      if (response.data.success) {
        showNotification(response.data.message || 'Category deleted successfully');
        loadJobCategories(); // Reload the list
      } else {
        showNotification('Failed to delete category', 'error');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      showNotification(error.message || 'Failed to delete category', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      setLoading(true);
      
      const response = selectedCategory 
        ? await crudOperations.put(`admin/job-categories/${selectedCategory.id}`, categoryData)
        : await crudOperations.post('admin/job-categories', categoryData);

      if (response.data.success) {
        showNotification(
          selectedCategory 
            ? 'Category updated successfully' 
            : 'Category created successfully'
        );
        loadJobCategories(); // Reload the list
        setShowAddForm(false);
        setShowEditForm(false);
        setSelectedCategory(null);
      } else {
        showNotification(response.data.error || 'Failed to save category', 'error');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      showNotification(error.message || 'Failed to save category', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
    setShowEditForm(false);
    setSelectedCategory(null);
  };

  const handleToggleProductActive = async (product) => {
    try {
      const response = await crudOperations.put(`admin/products-services/${product.id}/toggle-active`, {});
      if (response.data.success) {
        showNotification(response.data.message);
        loadProductsServices();
      }
    } catch (error) {
      console.error('Error toggling product active status:', error);
      showNotification(error.message || 'Failed to toggle active status', 'error');
    }
  };

  const handleDeleteProduct = async (product) => {
    const confirmed = await confirm({
      title: 'Delete Product',
      message: `This will permanently remove "${product.name}".`,
      variant: 'danger',
      confirmLabel: 'Delete'
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await crudOperations.delete(`admin/products-services/${product.id}`);

      if (response.data.success) {
        showNotification(response.data.message || 'Product/service deleted successfully');
        loadProductsServices(); // Reload the list
      } else {
        showNotification('Failed to delete product/service', 'error');
      }
    } catch (error) {
      console.error('Error deleting product/service:', error);
      showNotification(error.message || 'Failed to delete product/service', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      setLoading(true);
      
      const response = selectedProduct 
        ? await crudOperations.put(`admin/products-services/${selectedProduct.id}`, productData)
        : await crudOperations.post('admin/products-services', productData);

      if (response.data.success) {
        showNotification(
          response.data.message || (selectedProduct 
            ? 'Product/service updated successfully' 
            : 'Product/service created successfully')
        );
        loadProductsServices(); // Reload the list
        setShowAddProductForm(false);
        setShowEditProductForm(false);
        setSelectedProduct(null);
      } else {
        showNotification('Failed to save product/service', 'error');
      }
    } catch (error) {
      console.error('Error saving product/service:', error);
      showNotification(error.message || 'Failed to save product/service', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseProductForm = () => {
    setShowAddProductForm(false);
    setShowEditProductForm(false);
    setSelectedProduct(null);
  };

  const sectionButtons = [
    { id: 'categories', label: 'Job Categories', icon: FolderTree },
    { id: 'services', label: 'Invoice Products & Services', icon: Package },
    { id: 'treatmentCategories', label: 'Treatment Categories', icon: Tags },
    { id: 'treatmentProducts', label: 'Treatment Products', icon: Beaker }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Admin Settings</h2>
        <p className="text-gray-600">
          Administrative tools and configuration options. Access restricted to administrators.
        </p>
      </div>

      {/* Section Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {sectionButtons.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveSection(id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors duration-200 ${
                activeSection === id
                  ? 'border-pest-blue text-pest-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Section Content */}
      <div className="bg-gray-50 rounded-lg p-6">
        {activeSection === 'categories' && renderCategoryManagement()}
        
        {activeSection === 'services' && renderProductsServicesManagement()}

        {activeSection === 'treatmentCategories' && renderTreatmentCategoryManagement()}

        {activeSection === 'treatmentProducts' && renderTreatmentProductManagement()}
      </div>

      {/* Category Forms */}
      <CategoryForm
        isOpen={showAddForm}
        onClose={handleCloseForm}
        category={null}
        categories={categories}
        onSave={handleSaveCategory}
        loading={loading}
      />

      <CategoryForm
        isOpen={showEditForm}
        onClose={handleCloseForm}
        category={selectedCategory}
        categories={categories}
        onSave={handleSaveCategory}
        loading={loading}
      />

      {/* Product/Service Forms */}
      <ProductServiceForm
        isOpen={showAddProductForm}
        onClose={handleCloseProductForm}
        product={null}
        onSave={handleSaveProduct}
        loading={loading}
      />

      <ProductServiceForm
        isOpen={showEditProductForm}
        onClose={handleCloseProductForm}
        product={selectedProduct}
        onSave={handleSaveProduct}
        loading={loading}
      />

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 pt-8 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 flex items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pest-blue"></div>
            <p className="text-gray-700 font-medium">Processing...</p>
          </div>
        </div>
      )}
      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

export default AdminTab;